<template>
  <base-stack class="no-shows">
    <base-stack class="box" :spacing="3">
      <h3 class="box__title">No-show shipping</h3>
      <p class="box__description">
        When enabled, ticket holders who did not attend the secret gathering are emailed so they can confirm a shipping
        address for their package. Emails are sent two days after the show.
      </p>
      <base-toggle
        :value="tour?.meetAndGreet?.noShowShippingEnabled"
        :disabled="togglingShippingNotifications"
        @input="onToggleShippingAddressNotifications"
      >
        Send no-show shipping notifications
      </base-toggle>
      <base-toggle
        :value="tour?.meetAndGreet?.noShowShippingConfirmationsClosed"
        :disabled="togglingConfirmationsClosed"
        @input="onToggleConfirmationsClosed"
      >
        Close address submissions
      </base-toggle>
      <p v-if="tour?.meetAndGreet?.noShowShippingConfirmationsClosed" class="box__description">
        Address submissions are closed. Customers are directed to contact Citizen Relations instead.
      </p>
    </base-stack>
    <p v-if="$apollo.loading">Loading...</p>
    <p v-else-if="!tickets.length">No no-show tickets found.</p>
    <template v-else>
      <base-stack direction="row" justify="space-between" align="center">
        <p class="count">{{ tickets.length }} no-show ticket{{ tickets.length === 1 ? '' : 's' }}</p>
        <base-button @click="exportCsv">Export CSV</base-button>
      </base-stack>
      <table>
        <thead>
          <tr>
            <th>Show</th>
            <th>Date</th>
            <th>Customer</th>
            <th>Package</th>
            <th>Address status</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="ticket in tickets" :key="ticket.id">
            <td>{{ ticket.show?.address?.venue }}, {{ ticket.show?.address?.city }}</td>
            <td>{{ ticket.show?.showDate | dayjs.utc('MMM DD, YYYY') }}</td>
            <td>
              <router-link v-if="ticket.customer" :to="{ path: '/search', query: { q: ticket.customer.id } }">
                {{ ticket.customer.name || ticket.customer.email }}
              </router-link>
              <span v-else>Unknown</span>
            </td>
            <td>
              <ul v-if="ticket.package?.items?.length" class="package-items">
                <li v-for="item in ticket.package.items" :key="item.title">{{ item.quantity }}x {{ item.title }}</li>
              </ul>
              <span v-else>–</span>
            </td>
            <td>
              <span :class="addressStatusClass(ticket)">{{ addressStatus(ticket) }}</span>
            </td>
          </tr>
        </tbody>
      </table>
    </template>
  </base-stack>
</template>

<script lang="ts">
import { MeetAndGreetTicket, Tour } from '@/types/api';
import dayjs from 'dayjs';
import gql from 'graphql-tag';
import { Component, Prop, Vue } from 'vue-property-decorator';

@Component({
  apollo: {
    tour: {
      query: gql`
        query TourNoShows($id: ID!) {
          tour(id: $id) {
            id
            meetAndGreet {
              noShowShippingEnabled
              noShowShippingConfirmationsClosed
              noShowTickets {
                id
                quota
                shippingAddressNotifiedAt
                shippingAddressConfirmedAt
                shippingAddressDeclinedAt
                show {
                  id
                  showDate
                  address {
                    venue
                    city
                  }
                }
                customer {
                  id
                  email
                  name
                  phone
                  ... on User {
                    username
                    address {
                      name
                      firstAddressLine
                      secondAddressLine
                      postalCode
                      city
                      administrativeArea
                      country {
                        id
                        name
                      }
                      phone
                    }
                  }
                  ... on MeetAndGreetTicketPayment {
                    address {
                      name
                      firstAddressLine
                      secondAddressLine
                      postalCode
                      city
                      administrativeArea
                      country {
                        id
                        name
                      }
                      phone
                    }
                  }
                }
                package(includeGifted: true) {
                  items {
                    title
                    quantity
                  }
                }
              }
            }
          }
        }
      `,
      variables() {
        return { id: this.tourId };
      },
      fetchPolicy: 'cache-and-network',
    },
  },
})
export default class TourNoShows extends Vue {
  @Prop({ required: true })
  public tourId!: string;

  tour?: Tour;
  togglingShippingNotifications = false;
  togglingConfirmationsClosed = false;

  get tickets(): MeetAndGreetTicket[] {
    return this.tour?.meetAndGreet?.noShowTickets ?? [];
  }

  addressStatus(ticket: MeetAndGreetTicket): string {
    if (ticket.shippingAddressConfirmedAt) return 'Confirmed';
    if (ticket.shippingAddressDeclinedAt) return 'Declined';
    if (ticket.shippingAddressNotifiedAt) return 'Pending';
    return 'Not notified';
  }

  async onToggleShippingAddressNotifications(enabled: boolean) {
    this.togglingShippingNotifications = true;
    try {
      await this.updateMeetAndGreet({ noShowShippingEnabled: enabled });
    } catch (error) {
      this.$modal.alert({
        title: 'Error',
        message: `Failed to update: ${error instanceof Error ? error.message : 'Unknown error'}`,
      });
    } finally {
      this.togglingShippingNotifications = false;
    }
  }

  async onToggleConfirmationsClosed(closed: boolean) {
    this.togglingConfirmationsClosed = true;
    try {
      await this.updateMeetAndGreet({ noShowShippingConfirmationsClosed: closed });
    } catch (error) {
      this.$modal.alert({
        title: 'Error',
        message: `Failed to update: ${error instanceof Error ? error.message : 'Unknown error'}`,
      });
    } finally {
      this.togglingConfirmationsClosed = false;
    }
  }

  private updateMeetAndGreet(meetAndGreet: Record<string, boolean>) {
    return this.$apollo.mutate({
      mutation: gql`
        mutation UpdateTour($input: UpdateTourInput!) {
          updateTour(input: $input) {
            tour {
              id
              meetAndGreet {
                noShowShippingEnabled
                noShowShippingConfirmationsClosed
              }
            }
          }
        }
      `,
      variables: {
        input: {
          id: this.tourId,
          meetAndGreet,
        },
      },
    });
  }

  addressStatusClass(ticket: MeetAndGreetTicket): string {
    if (ticket.shippingAddressConfirmedAt) return 'status--confirmed';
    if (ticket.shippingAddressDeclinedAt) return 'status--declined';
    if (ticket.shippingAddressNotifiedAt) return 'status--pending';
    return 'status--not-notified';
  }

  private customerAddress(ticket: MeetAndGreetTicket): Record<string, string> {
    const customer = ticket.customer as Record<string, any> | null;
    const addr = customer?.address;
    return {
      addressName: addr?.name ?? '',
      firstAddressLine: addr?.firstAddressLine ?? '',
      secondAddressLine: addr?.secondAddressLine ?? '',
      postalCode: addr?.postalCode ?? '',
      city: addr?.city ?? '',
      administrativeArea: addr?.administrativeArea ?? '',
      country: addr?.country?.name ?? '',
      phone: addr?.phone ?? customer?.phone ?? '',
    };
  }

  exportCsv() {
    const headers = [
      'Show Venue',
      'Show City',
      'Show Date',
      'Customer Name',
      'Customer Email',
      'Customer Phone',
      'Package',
      'Address Status',
      'Address Name',
      'Address Line 1',
      'Address Line 2',
      'Postal Code',
      'City',
      'State/Province',
      'Country',
    ];

    const escape = (val: string) => `"${val.replace(/"/g, '""')}"`;

    const rows = this.tickets.map((ticket) => {
      const addr = this.customerAddress(ticket);
      const pkg = ticket.package?.items?.map((i) => `${i.quantity}x ${i.title}`).join(', ') ?? '';
      return [
        ticket.show?.address?.venue ?? '',
        ticket.show?.address?.city ?? '',
        ticket.show?.showDate ? dayjs.utc(ticket.show.showDate).format('YYYY-MM-DD') : '',
        ticket.customer?.name ?? '',
        ticket.customer?.email ?? '',
        addr.phone,
        pkg,
        this.addressStatus(ticket),
        addr.addressName,
        addr.firstAddressLine,
        addr.secondAddressLine,
        addr.postalCode,
        addr.city,
        addr.administrativeArea,
        addr.country,
      ].map(escape);
    });

    const csv = [headers.map(escape), ...rows].map((r) => r.join(',')).join('\n');
    const a = document.createElement('a');
    a.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csv);
    a.download = 'no-show-tickets.csv';
    a.click();
  }
}
</script>

<style lang="scss" scoped>
.box {
  border: 1px solid $color-theme-red;
  padding: 16px;
}

.box__title {
  margin: 0;
  color: $color-theme-red;
  font-size: 1rem;
  font-weight: 600;
}

.box__description {
  margin: 0;
  font-size: 0.9rem;
  line-height: 1.45;
  color: $color-theme-red;
  font-style: italic;
  max-width: 52rem;
}

.no-shows {
  padding: 1rem 0;
}

.count {
  font-size: 0.9rem;
  color: $color-theme-red;
  font-style: italic;
}

table {
  width: 100%;
  border-collapse: collapse;

  th,
  td {
    border: 1px solid $color-theme-red;
    padding: 8px;
    text-align: left;
  }

  th {
    background-color: #111;
    font-weight: bold;
  }

  tr:nth-child(even) {
    background-color: #111;
  }
}

.package-items {
  list-style: none;
  padding: 0;
  margin: 0;
  font-size: 0.9em;

  li:not(:last-child) {
    margin-bottom: 2px;
  }
}

.status {
  &--confirmed {
    color: $color-success;
  }
  &--declined,
  &--not-notified {
    color: $color-theme-red;
  }
  &--pending {
    color: $color-theme-yellow;
  }
}
</style>
